import React, { useState, useContext, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { getAllOrganizations, getCurrentOrganization, setAllOrganizations, setCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { OrganizationContext } from "@/context/providers/OrganizationProvider";
import { useNavigate } from "react-router";
import { HEADINGS, SWITCH_ORGANIZATION_MODAL } from "@/utils/constants/appConstants/commonConstants";
import { Organization } from "@/utils/interfaces/organization.interface";
import { SUFFIX_PERSONAL } from "@/utils/constants/constants";
import { getemail_address, getName } from "@/utils/functions/TokenFunction";
import { NO_ORGANIZATION_FOUND, ORG_DOMAIN_RESTRICTED } from "@/utils/constants/messages";
import { fetchConfig } from "@/services/setting.service";
import { getFromLocalStorage } from "@/utils/functions/helperFunctions";
import { errorToasterMessage } from "@/utils/functions/toaster";
import { getLoggedInUser } from "@/services/user.service";

interface SwitchOrganizationModalProps {
    show?: boolean;
    onHide?: () => void;
}

const SwitchOrganizationModal = (props: SwitchOrganizationModalProps) => {
    const [currOrganization, setCurrOrganization] = useState(getCurrentOrganization());
    const [organizations, setOrganizations] = useState<Organization[]>(getAllOrganizations() ?? []);
    const { setOrganization } = useContext(OrganizationContext);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [loadingOrganizationId, setLoadingOrganizationId] = useState<string>("");
    const navigate = useNavigate();
    const name = getName();
    const activeOrganizations = organizations.filter((org: Organization) => org?.is_active);
    const shouldScrollBody = activeOrganizations.length > SWITCH_ORGANIZATION_MODAL.MAX_VISIBLE_ORGANIZATIONS;
    const scrollBodyMaxHeight = `${SWITCH_ORGANIZATION_MODAL.MAX_VISIBLE_ORGANIZATIONS * SWITCH_ORGANIZATION_MODAL.ROW_HEIGHT_REM}rem`;

    useEffect(() => {
        if (!props.show) return;
        getLoggedInUser(
            (res: any) => {
                const orgs = res?.organizations ?? [];
                setAllOrganizations(orgs);
                setOrganizations(orgs);
                setCurrOrganization(getCurrentOrganization());
            },
            (err: any) => console.log(err)
        );
    }, [props.show]);

    const changeOrganizationHandler = (e: React.MouseEvent<HTMLElement>, organization: Organization) => {
        e.preventDefault();
        setLoadingConfig(true);
        const email_domain = getemail_address()?.split('@')[1].toLowerCase();
        setLoadingOrganizationId(organization.organization);
        fetchConfig({ id: organization.organization }, (res: any) => {
            setLoadingConfig(false);
            localStorage.removeItem('sso_login_enabled')
            localStorage.removeItem('sso_login_restricted')
            localStorage.setItem('isDocumentSearchEnabled', res?.is_document_search_enabled ?? false);
            if (res?.is_directory_service_enabled) {
                localStorage.setItem('sso_login_enabled', JSON.stringify(res?.is_directory_service_enabled));
                if (res?.is_restrict_login_with_sso) {
                    localStorage.setItem('sso_login_restricted', JSON.stringify(res?.is_restrict_login_with_sso));
                }
            }
            if (res?.is_signup_domain_restricted) {
                const allowed_domains = res?.allowed_domains.map((domain: string) => domain.toLowerCase());
                if (!allowed_domains.includes(email_domain)) {
                    errorToasterMessage(ORG_DOMAIN_RESTRICTED(res?.allowed_domains.join(', ')));
                    setLoadingOrganizationId('');
                    return;
                }
            }
            if ((!res?.is_directory_service_enabled && !getFromLocalStorage('loginWithSSO')) || (res?.is_directory_service_enabled && !getFromLocalStorage('loginWithSSO') && !res?.is_restrict_login_with_sso)) {
                navigate("/dashboard");
                setCurrOrganization(organization);
                setOrganization(organization);
                setCurrentOrganization(organization);
            } else {
                navigate(`/login?organization=${organization.organization}`);
            }
            props.onHide?.();
        }, (err: any) => console.log(err));
    };

    return (
        <Dialog open={props.show} onOpenChange={(open) => { if (!open) props.onHide?.(); }}>
            <DialogContent className="gs-switch-org-dialog">
                <div className="gs-switch-org-dialog__header">
                    <h4 id="switch-organization-modal-heading" className="gs-switch-org-dialog__title">
                        {HEADINGS.MY_ORGANIZATIONS}
                    </h4>
                </div>
                <div
                    className={`gs-switch-org-dialog__body${shouldScrollBody ? " gs-switch-org-dialog__body--scroll" : ""}`}
                    style={shouldScrollBody ? { maxHeight: scrollBodyMaxHeight } : undefined}
                >
                    {activeOrganizations.length > 0 ? (
                        activeOrganizations.map((org: Organization, key: any) => (
                            <div key={key} className="gs-switch-org-dialog__row">
                                <em
                                    id={`switch-organization-modal-icon-${key}`}
                                    className={`icon ni ni-${org?.name === "GLOBAL" ? "user-alt" : "building"} gs-switch-org-dialog__icon`}
                                    aria-hidden
                                />
                                <span id={`switch-organization-modal-name-${key}`} className="gs-switch-org-dialog__name">
                                    {org?.name === "GLOBAL" ? name + SUFFIX_PERSONAL : org?.name}
                                </span>
                                <div className="gs-switch-org-dialog__action">
                                    {org?.organization === currOrganization?.organization ? null : loadingConfig && loadingOrganizationId === org.organization ? (
                                        <Spinner size="sm" className="text-primary" />
                                    ) : !loadingConfig ? (
                                        <button
                                            id={`switch-organization-modal-switch-btn-${key}`}
                                            type="button"
                                            className="gs-switch-org-dialog__switch"
                                            onClick={(e) => changeOrganizationHandler(e, org)}
                                        >
                                            Switch
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="gs-switch-org-dialog__empty" id="switch-organization-modal-no-data-msg">
                            {NO_ORGANIZATION_FOUND}
                        </p>
                    )}
                </div>
                <div className="gs-switch-org-dialog__footer" />
            </DialogContent>
        </Dialog>
    );
};

export default SwitchOrganizationModal;
